/**
 * `#uc2Panel-practices`' own content (design `2026-10-02-uc2-tabs-analytics-design.md`
 * §Tabs, re-presented after the client round of 2 October 2026): what the watersheds with
 * positive change report that the rest do not.
 *
 * One block now, not three:
 *
 * * `#uc2PracticesByClass` — the four practice groups, one row each, two bars (**Positive
 *   areas** against **Other areas**) and the difference printed after them as "+1.4
 *   points" or "−4.0 points", **sorted by that difference** so the group that separates
 *   the two most is the first row a reader meets;
 * * the clicked watershed, where there is one, as a **third marker on every row** with its
 *   own legend entry — it used to be a second chart below (`#uc2PracticesUnit`), which
 *   asked the reader to hold four numbers in their head and look down to compare them
 *   with four more. A tick on the row it belongs to is the same fact, in the place the
 *   comparison is actually made;
 * * `#uc2Prevailing` — the takeaway, in one sentence, naming the group most ahead in the
 *   positive areas and by how much (or saying plainly that none is ahead). It used to be a
 *   comma-separated list of group names, which is a fact about the chart rather than a
 *   reading of it.
 *
 * Everything is read from `uc2/summary`'s `adoption_by_class` for the current scope plus
 * the detail the click already fetched. A run that carries no block prints a note.
 *
 * Association only: the chart says the two groups report different things, never that one
 * of them follows from the other — the method tab carries that sentence verbatim.
 */
import { useTranslation } from 'react-i18next';

import type { IntelUc2AdoptionScope, IntelUnitDetail } from '../../lib/api';
import { fmtFixed } from '../../lib/format';
import { PairedBars, type PairedBarsRow } from './charts/PairedBars';
import { practicesTopCaption } from './uc2Captions';
import {
  UC2_CATEGORY_COLORS,
  UC2_PRACTICE_GROUPS,
  UC2_REST_COLOR,
  byValueDesc,
} from './uc2Constants';

export interface Uc2PracticesProps {
  /** `summary.adoption_by_class[scope]`, or `undefined` while the run carries none. */
  adoption?: IntelUc2AdoptionScope | undefined;
  /** The clicked watershed's detail, or `null`. A country row has no adoption of its own. */
  detail: IntelUnitDetail | null;
  /**
   * `by_class_series[scope].n.positive` — how many watersheds the positive column is
   * measured over, which the caption names so a difference in points is read against the
   * size of the group it came from.
   */
  nPositive?: number | null;
}

/** "+1.4" / "−4.0" — the typographic minus (U+2212), not a hyphen, before a number. */
function signed(value: number): string {
  return `${value < 0 ? '−' : '+'}${fmtFixed(Math.abs(value), 1)}`;
}

export function Uc2Practices({ adoption, detail, nPositive }: Uc2PracticesProps) {
  const { t } = useTranslation();
  const watershed = detail && detail.unit.level !== 'admin0' ? detail : null;
  const label = (g: string) => t(`intel.uc2.practice.${g}`);
  const positive = adoption?.positive ?? {};
  const rest = adoption?.no_pattern ?? {};
  const diff = adoption?.diff ?? {};

  const numeric = (v: number | null | undefined) =>
    typeof v === 'number' && Number.isFinite(v) ? v : null;

  /** The difference the row prints and sorts on: the block's own, else the two bars'. */
  const differenceOf = (g: string, a: number | null, b: number | null): number | null => {
    const stored = numeric(diff[g]);
    if (stored != null) return stored;
    return a != null && b != null ? a - b : null;
  };

  const rows: PairedBarsRow[] = UC2_PRACTICE_GROUPS.map((g) => {
    const a = numeric(positive[g]);
    const b = numeric(rest[g]);
    const d = differenceOf(g, a, b);
    const mine = watershed ? numeric(watershed.adoption?.[g]) : null;
    return {
      id: g,
      label: label(g),
      a,
      b,
      ...(d != null
        ? { valueText: t(Math.abs(d) === 1 ? 'intel.uc2.diffPoint' : 'intel.uc2.diffPoints', {
            value: signed(d),
          }) }
        : {}),
      ...(mine != null ? { marker: mine } : {}),
    };
  })
    .filter((row) => row.a != null || row.b != null)
    .sort((x, y) => byValueDesc(diff[x.id], diff[y.id]));

  const caption = practicesTopCaption(diff, label, nPositive);

  /**
   * The takeaway. Read off the same `diff` the rows are sorted by, so the sentence always
   * names the row at the top; a block where nothing is ahead says so rather than naming
   * whichever group is least behind, which would be the sentence contradicting its own
   * chart.
   */
  const ahead = UC2_PRACTICE_GROUPS.map((g) => ({ group: g as string, points: numeric(diff[g]) }))
    .filter((e): e is { group: string; points: number } => e.points != null && e.points > 0)
    .sort((x, y) => y.points - x.points)[0];
  const takeaway = ahead
    ? t(
        ahead.points === 1 ? 'intel.uc2.practicesTakeawayPoint' : 'intel.uc2.practicesTakeaway',
        { practice: label(ahead.group), points: fmtFixed(ahead.points, 1) },
      )
    : t('intel.uc2.practicesTakeawayNone');

  return (
    <div id="uc2PracticesByClass" className="uc2-chart intel-card">
      <h6 className="intel-card-title">{t('intel.uc2.chart.practicesByClass')}</h6>
      {rows.length ? (
        <>
          <PairedBars
            rows={rows}
            labels={[t('intel.uc2.positiveAreas'), t('intel.uc2.otherAreas')]}
            unit="%"
            colors={[UC2_CATEGORY_COLORS.positive!, UC2_REST_COLOR]}
            ariaLabel={t('intel.uc2.chart.practicesByClass')}
            {...(watershed ? { markerLabel: watershed.unit.name } : {})}
          />
          {caption ? (
            <p className="sub" data-testid="uc2-practices-caption">
              {t(caption.key, caption.options ?? {})}
            </p>
          ) : null}
          <p id="uc2Prevailing" className="sub" data-testid="uc2-practices-takeaway">
            {takeaway}
          </p>
        </>
      ) : (
        <p className="note" data-testid="uc2-practices-empty">
          {t('intel.uc2.blockPending')}
        </p>
      )}
    </div>
  );
}

export default Uc2Practices;
